import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode, forwardRef, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { LinkBox, LinkOverlay } from 'atoms/navigation/Box';
import { glossary } from 'lib/glossary';

import { Collapse } from '../Collapse';

type Props = {
  title: ReactNode;
  children:
    | ReactNode
    | React.FC<React.PropsWithChildren<{ expanded: boolean }>>;
  startsOpen?: boolean;
  noBorder?: boolean;
  noBorderWhenExpanded?: boolean;
  noHorizontalPadding?: boolean;
  noTopPadding?: boolean;
  noContentPadding?: boolean;
  onExpandChange?: (expand: boolean) => void;
  isFilter?: boolean;
};

const Root = styled.div<{
  noBorder?: boolean;
}>`
  display: flex;
  flex-direction: column;
  color: var(--c-white);

  ${({ noBorder }) =>
    !noBorder &&
    css`
      border-bottom: 1px solid var(--c-nd-50);
    `}
`;

export const StyledLinkBox = styled(LinkBox)<{
  $noHorizontalPadding?: boolean;
  $noTopPadding?: boolean;
  $isFilter: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  --horizontal-padding: ${({ $noHorizontalPadding }) =>
    $noHorizontalPadding ? '0' : 'var(--double-unit)'};
  --top-padding: ${({ $noTopPadding }) =>
    $noTopPadding ? '0' : 'var(--intermediate-unit)'};
  padding: var(--top-padding) var(--horizontal-padding);
  gap: var(--double-unit);

  ${({ $isFilter }) =>
    $isFilter
      ? css`
          transition: all 0.3s;
          &:hover {
            background-color: var(--c-nd-100);
          }
        `
      : ''}
`;

const Details = styled.div<{
  $noContentPadding?: boolean;
  $noHorizontalPadding?: boolean;
  $noTopPadding?: boolean;
  $isFilter?: boolean;
}>`
  --padding: var(--double-unit);
  ${({ $noTopPadding }) =>
    `--top-padding: ${$noTopPadding ? '0' : 'var(--intermediate-unit)'};`}

  ${({ $noHorizontalPadding, $noContentPadding }) => {
    if ($noHorizontalPadding) {
      return css`
        padding: var(--top-padding) 0 var(--padding) 0;
      `;
    }
    if ($noContentPadding) {
      return css`
        padding: 0;
      `;
    }
    return css`
      padding: var(--top-padding) var(--padding) var(--padding);
    `;
  }}

  ${({ $isFilter, $noContentPadding }) =>
    !$isFilter && !$noContentPadding
      ? css`
          padding: var(--double-unit);
        `
      : ''}
`;

const Icon = styled(FontAwesomeIcon)<{ $expanded: boolean }>`
  color: var(--c-nd-600-opaque);
  fill: var(--c-nd-600);
  transform: rotate(0deg);
  transition: 0.3s transform;

  ${({ $expanded }) =>
    $expanded &&
    css`
      transform: rotate(-180deg);
    `}
`;

export const Accordion = forwardRef<HTMLDivElement, Props>(
  (
    {
      title,
      startsOpen = false,
      noBorder,
      noBorderWhenExpanded,
      noHorizontalPadding,
      noTopPadding,
      noContentPadding,
      children,
      onExpandChange,
      isFilter = false,
    }: Props,
    ref
  ) => {
    const [expanded, setExpanded] = useState(startsOpen);
    const { formatMessage } = useIntl();
    const [renderCollapseContent, setRenderCollapseContent] =
      useState(expanded);
    const id = useId();

    if (expanded !== renderCollapseContent) {
      setRenderCollapseContent(expanded);
    }

    return (
      <Root
        ref={ref}
        noBorder={(noBorderWhenExpanded && !expanded) || noBorder}
      >
        <StyledLinkBox
          $noTopPadding={noTopPadding}
          $noHorizontalPadding={noHorizontalPadding}
          id={id}
          $isFilter={isFilter}
        >
          {title}
          <LinkOverlay
            as="button"
            aria-labelledby={id}
            onClick={() => {
              onExpandChange?.(!expanded);
              if (!expanded) {
                setRenderCollapseContent(!expanded);
              }
              setExpanded(!expanded);
            }}
          >
            <Icon
              icon={faChevronDown}
              $expanded={expanded}
              height={16}
              aria-label={formatMessage(
                expanded ? glossary.showLess : glossary.showMore
              )}
            />
          </LinkOverlay>
        </StyledLinkBox>
        <Collapse open={expanded} gap={0}>
          <Details
            $noContentPadding={noContentPadding}
            $noHorizontalPadding={noHorizontalPadding}
            $noTopPadding={noTopPadding}
            $isFilter={isFilter}
          >
            {typeof children === 'function'
              ? children({ expanded: renderCollapseContent })
              : children}
          </Details>
        </Collapse>
      </Root>
    );
  }
);

Accordion.displayName = 'Accordion';

export const FilterAccordion = ({
  title,
  children,
  startsOpen,
  noTopPadding,
  noHorizontalPadding,
}: {
  title?: ReactNode;
  children:
    | ReactNode
    | React.FC<React.PropsWithChildren<{ expanded: boolean }>>;
  startsOpen?: boolean;
  noTopPadding?: boolean;
  noHorizontalPadding?: boolean;
}) => {
  if (title) {
    return (
      <Accordion
        title={title}
        startsOpen={startsOpen}
        noBorder
        noBorderWhenExpanded
        isFilter
        noTopPadding={noTopPadding}
        noHorizontalPadding={noHorizontalPadding}
      >
        {({ expanded }): ReactNode =>
          typeof children === 'function' ? children({ expanded }) : children
        }
      </Accordion>
    );
  }

  return (
    <Root noBorder>
      <Details $noHorizontalPadding>
        {typeof children === 'function'
          ? children({ expanded: true })
          : children}
      </Details>
    </Root>
  );
};
